import { Component, signal } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import readXlsxFile from 'read-excel-file/browser';
import writeXlsxFile from 'write-excel-file/browser';
import { MassiveServicesService } from '../services/massive-services.service';

@Component({
  selector: 'app-massive-services-upload',
  imports: [CommonModule, FormsModule],
  templateUrl: './massive-services-upload.html',
  styleUrl: './massive-services-upload.css',
})
export class MassiveServicesUpload {
  excelFilePath = signal<string>('');
  errorMessage = signal<string>('');
  successMessage = signal<string>('');
  isSending = signal<boolean>(false);
  isValidExcel = signal<boolean>(false);

  orderRequestId = '';
  portfolioId = '';
  processManagerId = '';
  forceLaunchToExchange = false;

  readonly REQUIRED_HEADERS = [
    'SOLICITUD', 'REF_EXTERNA', 'Columna1', 'Columna2', 'Columna3', 'Columna4',
    'TECNICO', 'Columna5', 'Columna6', 'Columna7', 'Columna8', 'Columna9',
    'Columna10', 'Columna11', 'Columna12', 'Columna13', 'Columna14', 'Columna15',
    'REFERENCIA_CATASTRAL', 'Columna16', 'Columna17', 'Columna18', 'Columna19',
    'Columna20', 'Columna21', 'Columna22', 'Columna23', 'Columna24', 'Columna25',
    'Columna26', 'HONORARIOS_TECNICOS', 'IMPORTE', 'NOTA_SIMPLE'
  ];

  private selectedExcelFile: File | null = null;

  constructor(private router: Router, private massiveServicesService: MassiveServicesService) {}

  goBack() {
    this.router.navigate(['/home']);
  }

  selectExcelFile() {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', '.xlsx,.xls');

    input.onchange = async (event: Event) => {
      const target = event.target as HTMLInputElement;
      const file = target.files?.[0];
      if (file) {
        this.selectedExcelFile = file;
        this.excelFilePath.set(file.name);
        this.errorMessage.set('');
        this.successMessage.set('');
        this.isValidExcel.set(false);
        await this.validateExcelHeaders(file);
      }
    };

    input.click();
  }

  async validateExcelHeaders(file: File): Promise<void> {
    try {
      const rows = await readXlsxFile(file);
      if (rows.length === 0) {
        this.errorMessage.set('El Excel no tiene el formato correcto');
        return;
      }
      const headers = rows[0].map(cell => (cell !== null && cell !== undefined ? String(cell) : ''));
      const headersMatch =
        headers.length === this.REQUIRED_HEADERS.length &&
        headers.every((h, i) => h === this.REQUIRED_HEADERS[i]);
      if (!headersMatch) {
        this.errorMessage.set('El Excel no tiene el formato correcto');
      } else {
        this.isValidExcel.set(true);
      }
    } catch {
      this.errorMessage.set('El Excel no tiene el formato correcto');
    }
  }

  canSend(): boolean {
    return !!this.orderRequestId.trim() && !!this.selectedExcelFile && this.isValidExcel() && !this.isSending();
  }

  async downloadExampleExcel(): Promise<void> {
    const EXAMPLE_ROW = [
      '100001', 'REF-EXT-001', '', '', '', '',
      'TECNICO01', '', '', '', '', '',
      '', '', '', '', '', '',
      '1234567VH1234A0001', '', '', '', '',
      '', '', '', '', '', '',
      '', '500', '1000', 'SN'
    ];
    const data = [this.REQUIRED_HEADERS, EXAMPLE_ROW];
    await writeXlsxFile(data, { fileName: 'ejemplo_servicios_masivos.xlsx' });
  }

  async sendRequest() {
    if (!this.canSend()) {
      return;
    }

    this.isSending.set(true);
    this.errorMessage.set('');
    this.successMessage.set('');

    try {
      await firstValueFrom(
        this.massiveServicesService.createByMassiveExcel({
          orderRequestId: this.orderRequestId.trim(),
          excelFile: this.selectedExcelFile!,
          portfolioId: this.portfolioId.trim() || undefined,
          processManagerId: this.processManagerId.trim() || undefined,
          forceLaunchToExchange: this.forceLaunchToExchange
        })
      );
      this.successMessage.set('Solicitud de alta masiva de servicios enviada correctamente');
    } catch (error: any) {
      const message = error.error?.message || error.message || 'Error desconocido al enviar la solicitud';
      this.errorMessage.set(`Error en el envío: ${message}`);
    } finally {
      this.isSending.set(false);
    }
  }
}
